import { Channel } from "components/chat/Channel";
import Async from "components/DesignSystem/AsyncComponents/Async";
import Modal from "components/DesignSystem/Modal/Modal";
import { useChannelNewMessageListener } from "hooks/useChannelNewMessageListener";
import { useCurrentEntity } from "hooks/useCurrentEntity";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useUpdateQuery } from "hooks/useQuery";
import { useToast } from "hooks/useToast";
import { useHistory } from "react-router-dom";
import { useLazyGetAgentSessionQuery } from "store/apis/inkleAssistant";
import { BackendError } from "types/utils/error";
import { CompleteWithAIHeader } from "v2/components/CompleteWithAI/CompleteWithAIHeader";
import { DashboardEntity } from "v2/pages/FilingsDashboard/FilingsDashboard";
import { useGetTaxYearsQuery } from "v2/store/taxSetupApi";
import { useOnboardingProgress } from "v2/utils/useOnboardingProgress";
import { DetectedFilings } from "./DetectedFilings";

export const CompleteWithAILayout = () => {
  const [getAgentSession] = useLazyGetAgentSessionQuery();
  const { secondary_channel_url, uuid: groupId } = useCurrentGroupContext();
  const history = useHistory();
  const { update } = useUpdateQuery();
  const { alertToast } = useToast();

  const {
    data: taxYears,
    isLoading,
    isSuccess,
  } = useGetTaxYearsQuery({ groupId }, { skip: !groupId });
  const currentEntity = useCurrentEntity();
  const entityId = currentEntity.uuid || taxYears?.entities[0]?.uuid;

  const entities: DashboardEntity[] =
    taxYears?.entities.map((entity) => ({
      id: entity.uuid,
      name: entity.name,
      defaultYear: Number(entity.default_tax_year),
      pastYears: entity.past_tax_years.map(Number),
      trackedYears: entity.tracked_years.map(Number),
    })) || [];

  const { progressById, overallProgress, probes } = useOnboardingProgress({
    groupId,
    entityIds: entities.map((entity) => entity.id),
    polling: "3000",
  });

  const handleNewMessage = async () => {
    if (!secondary_channel_url) return;
    try {
      const agentSession = await getAgentSession({
        channelUrl: secondary_channel_url,
      }).unwrap();
      if (
        agentSession?.company_entity_id &&
        agentSession?.company_entity_id !== entityId
      ) {
        update({ value: agentSession?.company_entity_id, query: "entity" });
      }
    } catch (error) {
      alertToast({
        message: (error as BackendError).data?.error?.message,
      });
    }
  };

  useChannelNewMessageListener(secondary_channel_url, handleNewMessage);

  return (
    <Modal.Root open onOpenChange={history.goBack}>
      {probes}
      <Modal.Content size="fullscreen">
        <Async.Root
          isEmpty={entities.length === 0 || !entityId}
          isLoading={isLoading}
          isSuccess={isSuccess}
        >
          <Async.Empty>
            <div className="t-flex t-h-screen t-w-full t-items-center t-justify-center">
              <div className="t-flex t-flex-col t-items-center t-gap-3">
                <span className="t-text-subtext t-text-text-60">
                  No entities found for this group.
                </span>
              </div>
            </div>
          </Async.Empty>
          <Async.Success>
            <Modal.Header>
              <CompleteWithAIHeader
                entities={entities}
                onboardingProgress={overallProgress}
                progressById={progressById}
              />
              <Modal.Close />
            </Modal.Header>
            <Modal.Body className="!t-p-0 t-flex t-h-screen t-w-full t-justify-end">
              <div className="t-flex t-w-full t-flex-1 t-items-start t-overflow-hidden">
                <div className="t-w-full t-shrink t-h-full">
                  <Channel
                    Header={() => <></>}
                    messageMaxWidth="460px"
                    channelId={secondary_channel_url}
                  />
                </div>
                <div className="t-h-full t-w-2/5">
                  {entityId && (
                    <DetectedFilings
                      groupId={groupId}
                      entityId={entityId}
                      channelUrl={secondary_channel_url}
                      entities={entities}
                    />
                  )}
                </div>
              </div>
            </Modal.Body>
          </Async.Success>
        </Async.Root>
      </Modal.Content>
    </Modal.Root>
  );
};
